import type { GameCardView } from '../gameCard';

/**
 * Fun & Games: the game's card. It takes the host avatar's place above the
 * interviewer's message while a game is on: the game and round, the card
 * heading (the find, the award, the pair, the object), dice, and a Time
 * Machine year. About the avatar's height, so the message keeps its room.
 */
export function GameCard({ view }: { view: GameCardView }) {
  return (
    <section className="lp-game-card" aria-label={[view.name, view.progress].filter(Boolean).join(', ')}>
      <div className="lp-eyebrow-text lp-game-eyebrow">
        {view.progress ? `${view.name} · ${view.progress}` : view.name}
      </div>
      {view.era && <div className="lp-game-era">{view.era}</div>}
      {view.title && <div className="lp-game-title">{view.title}</div>}
      {view.dice.length > 0 && (
        <div className="lp-game-dice" role="group" aria-label={`Dice: ${view.dice.join(' and ')}`}>
          {view.dice.map((word) => (
            <span key={word} className="lp-game-die">
              {word}
            </span>
          ))}
        </div>
      )}
    </section>
  );
}

interface GameAnswersProps {
  view: GameCardView;
  /** Answers are held: the interviewer is preparing or speaking, or a voice
   *  answer is being recorded. */
  disabled: boolean;
  /** The interview has ended (END_CHAT): show how it went, take no answers. */
  ended: boolean;
  /** Sends a choice's text, or an action's reply, as the answer: the same
   *  turn as typing it and tapping Send. */
  onReply: (text: string) => void;
  /** Opens the attachment picker, whose "Upload as Answer" sends the photo as
   *  this turn's answer. Absent until uploads are possible (no category yet). */
  onAddPhoto?: () => void;
  /** The finishing bonus opens the same credits explainer as the top bar. */
  onCreditsInfo: () => void;
}

/**
 * Fun & Games: under the interviewer's message, the ways to answer it besides
 * talking, recording or typing (which all work as in any interview): choices
 * to tap, a photo when one is the best answer, and the game's own buttons.
 * Once there is one, the group's results and the finishing bonus.
 */
export function GameAnswers({ view, disabled, ended, onReply, onAddPhoto, onCreditsInfo }: GameAnswersProps) {
  const answering = !ended && !view.finished;
  const showActions = answering && (view.wantsPhoto || view.actions.length > 0);
  return (
    <div className="lp-game-answers">
      {answering && view.choices.length > 0 && (
        <div className="lp-game-choices">
          {view.choices.map((choice) => (
            <button
              key={choice}
              type="button"
              onClick={() => onReply(choice)}
              disabled={disabled}
              className="lp-btn lp-game-choice"
            >
              {choice}
            </button>
          ))}
        </div>
      )}

      {view.results.length > 0 && (
        <ul className="lp-game-results">
          {view.results.map((result) => (
            <li key={result.title}>
              <span className="lp-eyebrow-text">{result.title}</span>
              <span className="lp-game-winner">
                <span>{result.winner ?? 'No votes yet'}</span>
                {result.votes && <span className="lp-game-votes">{result.votes}</span>}
              </span>
            </li>
          ))}
        </ul>
      )}

      {view.bonus && (
        <button
          type="button"
          onClick={onCreditsInfo}
          className="lp-frost-chip lp-credits-chip lp-game-bonus"
          title="What are credits?"
        >
          <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden>
            <path d="M12 2l2.2 6.6L21 11l-6.8 2.4L12 20l-2.2-6.6L3 11l6.8-2.4z" />
          </svg>
          <span>{view.bonus}</span>
        </button>
      )}

      {showActions && (
        <div className="lp-game-actions">
          {view.wantsPhoto && (
            <button
              type="button"
              onClick={onAddPhoto}
              disabled={disabled || !onAddPhoto}
              className="lp-btn lp-btn-primary"
            >
              <svg
                viewBox="0 0 24 24"
                width="15"
                height="15"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
              >
                <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z" />
                <circle cx="12" cy="13" r="3" />
              </svg>
              <span>Add a photo</span>
            </button>
          )}
          {view.actions.map((action) => (
            <button
              key={`${action.label}:${action.reply}`}
              type="button"
              onClick={() => onReply(action.reply)}
              disabled={disabled}
              className="lp-btn-link"
            >
              {action.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
